import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  getArticles,
  getArticlesByAuthor,
  deleteArticle,
} from '../../services/firebase';
import type { Article } from '../../types';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Badge } from '../common/Badge';

interface ArticleListProps {
  userUid?: string;
  isAdmin: boolean;
  onEdit: (article: Article) => void;
  onCreate: () => void;
}

export function ArticleList({ userUid, isAdmin, onEdit, onCreate }: ArticleListProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<Article | null>(null);

  const loadArticles = useCallback(async () => {
    try {
      const data = isAdmin
        ? await getArticles()
        : userUid
          ? await getArticlesByAuthor(userUid)
          : [];
      setArticles(data);
    } catch (error) {
      console.error('Error loading articles:', error);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, userUid]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteArticle(deleteConfirm.id);
      toast.success('Article deleted');
      setDeleteConfirm(null);
      loadArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Failed to delete article');
    }
  };

  const isPromotionExpired = (article: Article): boolean => {
    if (!article.promoted || !article.promotedUntil) return false;
    return article.promotedUntil.toDate() < new Date();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Articles</h2>
          <p className="mt-1 text-sm text-gray-500">
            {isAdmin
              ? 'Manage all articles displayed in the mobile app.'
              : 'Manage your articles.'}
          </p>
        </div>
        <Button variant="primary" onClick={onCreate}>
          Add Article
        </Button>
      </div>

      {articles.length === 0 ? (
        <div className="text-center py-12 bg-white shadow rounded-lg text-gray-500">
          {isAdmin
            ? 'No articles yet. Click "Add Article" to create one.'
            : 'You haven\u2019t created any articles yet. Click "Add Article" to get started.'}
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Article
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {articles.map((article) => (
                <tr key={article.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {article.thumbnailUrl && (
                        <img
                          src={article.thumbnailUrl}
                          alt=""
                          className="h-10 w-10 rounded object-cover flex-shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {article.title}
                        </div>
                        <div className="text-xs text-gray-500">
                          {article.author.name}
                          {article.publishedDate && (
                            <> &middot; {article.publishedDate.toDate().toLocaleDateString()}</>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={article.type === 'external' ? 'default' : 'info'}>
                      {article.type}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {/* Review status — articles without reviewStatus that are
                          enabled predate the review workflow and are treated as accepted */}
                      {(() => {
                        const status = article.reviewStatus || (article.enabled ? 'accepted' : 'draft');
                        if (status === 'draft') return <Badge variant="default">Draft</Badge>;
                        if (status === 'ready_for_review') return <Badge variant="info">Pending Review</Badge>;
                        if (status === 'rejected') return <Badge variant="danger">Changes Requested</Badge>;
                        if (status === 'accepted') return <Badge variant="success">Accepted</Badge>;
                        return null;
                      })()}
                      {!article.enabled && (
                        <Badge variant="danger">Disabled</Badge>
                      )}
                      {article.featured && (
                        <Badge variant="info">Featured</Badge>
                      )}
                      {/* Gray, not an accent: every accent in this column is
                          already spoken for, and this reads as an attribute of
                          the article rather than a status of it. */}
                      {article.visibleWhenAnonymous && (
                        <Badge variant="default">Pre-signup</Badge>
                      )}
                      {article.promoted && (
                        isPromotionExpired(article) ? (
                          <Badge variant="warning">Promoted (Expired)</Badge>
                        ) : (
                          <Badge variant="warning">Promoted</Badge>
                        )
                      )}
                      {article.pinOrder != null && (
                        <Badge variant="default">Pinned #{article.pinOrder}</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => onEdit(article)}>
                        Edit
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => setDeleteConfirm(article)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Article"
      >
        <div className="p-2 space-y-4">
          <p className="text-sm text-gray-700">
            Are you sure you want to delete &ldquo;{deleteConfirm?.title}&rdquo;? This cannot be
            undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
